import React, { useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Box, Flex, Icons, Tag, Text, Theme, Tooltip } from 'baukasten';

import {
  MAX_ATTACHMENTS,
  MAX_DOCUMENT_BYTES,
  MAX_IMAGE_BYTES,
  MAX_TOTAL_ATTACHMENT_BYTES,
  MEBIBYTE,
  UPLOAD_ACCEPT,
} from '../constants';
import messages from './messages';
import { filesWithinUploadLimits } from '../utils/fileParts';
import { IconButton } from './IconButton';
import { VoiceButton } from './VoiceButton';

const Frame = styled(Box)`
  border: 1px solid ${Theme.getColor('grey-light-100')};
  border-radius: 12px;
  background-color: ${Theme.getColor('white')};

  &:focus-within {
    border-color: ${Theme.getColor('lilac')};
  }
`;

const Input = styled.textarea`
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  max-height: 96px;
  font-family: inherit;
  font-size: ${Theme.getFontSize(14)};
  color: ${Theme.getColor('navy')};
  background: transparent;

  &::placeholder {
    color: ${Theme.getColor('grey-dark')};
  }
`;

const SubmitButton = styled(IconButton)`
  background-color: ${Theme.getColor('navy')};

  &:hover:not(:disabled) {
    background-color: ${Theme.getColor('navy-dark')};
  }
`;

export type ComposerSubmission = {
  text: string;
  files: File[];
  isVoiceOnly: boolean;
};

type Props = {
  busy: boolean;
  onSubmit: (submission: ComposerSubmission) => Promise<void> | void;
  onStop?: () => void;
};

export const ChatComposer = ({ busy, onSubmit, onStop }: Props) => {
  const { formatMessage } = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isOverAttachmentLimit, setIsOverAttachmentLimit] = useState(false);
  const [isVoiceOnly, setIsVoiceOnly] = useState(false);
  // a transcript lands after an await, so it must read the text typed meanwhile
  const textRef = useRef(text);
  textRef.current = text;

  // a native textarea never grows with its content; size it from scrollHeight, the
  // CSS max-height caps it
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${input.scrollHeight}px`;
  }, [text]);

  const canSubmit = !busy && Boolean(text.trim() || files.length);
  const isSubmitDisabled = !onStop && !canSubmit;

  const submit = async () => {
    if (!canSubmit) return;
    const sentText = text;
    const sentFiles = files;
    try {
      await onSubmit({ text: sentText.trim(), files: sentFiles, isVoiceOnly });
    } catch {
      return;
    }
    // attachments encode before the send resolves; keep anything typed meanwhile
    setText(current => (current === sentText ? '' : current));
    setFiles(current => (current === sentFiles ? [] : current));
    setIsOverAttachmentLimit(false);
    setIsVoiceOnly(false);
  };

  const insertTranscript = (transcript: string) => {
    setIsVoiceOnly(!textRef.current);
    setText(current => (current ? `${current} ${transcript}` : transcript));
  };

  return (
    <Frame px={3} py={2}>
      {files.length > 0 && (
        <Flex gap={1} mb={2} flexWrap="wrap">
          {files.map(file => (
            <Tag
              key={file.name}
              label={file.name}
              truncated
              suffixIcon={Icons.Close}
              ariaLabel={formatMessage(messages.composerRemoveAttachment, {
                name: file.name,
              })}
              // the whole chip removes: baukasten's suffix-icon slot has no accessible name
              onClick={() =>
                setFiles(current => current.filter(item => item !== file))
              }
            />
          ))}
        </Flex>
      )}
      {isOverAttachmentLimit && (
        <Text.SmallDescription color="red-dark" mb={2}>
          {formatMessage(messages.composerAttachLimit, {
            count: MAX_ATTACHMENTS,
            totalMb: MAX_TOTAL_ATTACHMENT_BYTES / MEBIBYTE,
            imageMb: MAX_IMAGE_BYTES / MEBIBYTE,
            documentMb: MAX_DOCUMENT_BYTES / MEBIBYTE,
          })}
        </Text.SmallDescription>
      )}
      <Input
        ref={inputRef}
        rows={1}
        value={text}
        placeholder={formatMessage(messages.composerPlaceholder)}
        onChange={event => {
          setIsVoiceOnly(false);
          setText(event.target.value);
        }}
        onKeyDown={event => {
          // Enter also confirms an IME candidate; only a composed Enter submits
          if (
            event.key === 'Enter' &&
            !event.shiftKey &&
            !event.nativeEvent.isComposing
          ) {
            event.preventDefault();
            submit();
          }
        }}
      />
      <Flex justifyContent="space-between" alignItems="center" mt={1}>
        <Flex gap={1} alignItems="center">
          <Tooltip tooltipContent={formatMessage(messages.composerAttach)}>
            <IconButton
              type="button"
              aria-label={formatMessage(messages.composerAttach)}
              onClick={() => fileInputRef.current?.click()}
            >
              <Icons.Attachment color="grey-dark" size="20px" />
            </IconButton>
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            accept={UPLOAD_ACCEPT}
            onChange={event => {
              const selectedFiles = Array.from(event.target.files ?? []);
              const isAllowed = filesWithinUploadLimits(selectedFiles);
              setFiles(isAllowed ? selectedFiles : []);
              setIsOverAttachmentLimit(!isAllowed);
              // clear the input so removing a file and re-picking it still fires change
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
          <VoiceButton disabled={busy} onTranscript={insertTranscript} />
        </Flex>
        <SubmitButton
          type="button"
          disabled={isSubmitDisabled}
          aria-label={formatMessage(
            onStop ? messages.composerStop : messages.composerSubmit
          )}
          onClick={onStop ?? submit}
        >
          {onStop ? (
            <Box
              width={Theme.getBaseUnit(3)}
              height={Theme.getBaseUnit(3)}
              borderRadius={2}
              backgroundColor="white"
            />
          ) : (
            <Icons.Send color="white" size="14px" />
          )}
        </SubmitButton>
      </Flex>
    </Frame>
  );
};
